import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Mail, Send, CheckCircle2, Loader2, User, Package, CreditCard, Clock, RefreshCcw, FileText, ExternalLink, DollarSign } from 'lucide-react';
import { formatCurrency } from '@/lib/translations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SendToCustomerSection = ({ formData, onSendToCustomer, isSubmitting, t, language }) => {
  const [personalMessage, setPersonalMessage] = useState('');
  const [contractId, setContractId] = useState(null);
  const [signingUrl, setSigningUrl] = useState(null);
  const [contractStatus, setContractStatus] = useState(null);
  const [isSent, setIsSent] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSendingPayment, setIsSendingPayment] = useState(false);
  const [paymentSent, setPaymentSent] = useState(false);
  const [statusError, setStatusError] = useState(null);
  
  const locale = language === 'de' ? 'de-DE' : 'en-US';
  const send = t.sendToCustomer;

  // Poll for contract status after sending
  const checkStatus = useCallback(async () => {
    if (!contractId) return;
    try {
      const res = await fetch(`${API_URL}/api/contract/${contractId}/status`);
      if (res.ok) {
        const data = await res.json();
        setContractStatus(data);
        if (data.status === 'payment_sent') {
          setPaymentSent(true);
        }
      }
    } catch {
      setStatusError('Failed to check status');
    }
  }, [contractId]);

  useEffect(() => {
    if (!contractId || !isSent) return;
    // Check immediately
    checkStatus();
    // Then poll every 10 seconds
    const interval = setInterval(checkStatus, 10000);
    return () => clearInterval(interval);
  }, [contractId, isSent, checkStatus]);

  const handleSend = async () => {
    if (!formData.customerInfo.email) return;
    setIsSending(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contract/create-pending`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerInfo: {
            customerNumber: formData.customerInfo.customerNumber || '',
            salutation: formData.customerInfo.salutation || '',
            name: formData.customerInfo.name,
            companyName: formData.customerInfo.companyName || '',
            commercialRegister: formData.customerInfo.commercialRegister || '',
            vatId: formData.customerInfo.vatId || '',
            address: formData.customerInfo.address,
            phone: formData.customerInfo.phone,
            email: formData.customerInfo.email,
          },
          productDetails: {
            quantity: formData.productDetails.quantity || 1,
            unitPrice: formData.productDetails.unitPrice || 58500,
            discount: formData.productDetails.discount || 0,
            discountAmount: formData.productDetails.discountAmount || 0,
            subtotal: formData.productDetails.subtotal || formData.productDetails.netPrice || 58500,
            vat: formData.productDetails.vat || formData.productDetails.vatAmount || 11115,
            totalPrice: formData.productDetails.totalPrice || 69615,
          },
          usePaymentPlan: formData.usePaymentPlan,
          personalMessage: personalMessage || null,
          language: language,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to send');
      }

      const data = await response.json();
      setContractId(data.contract_id);
      setSigningUrl(data.signing_url);
      setIsSent(true);
      
      if (onSendToCustomer) {
        onSendToCustomer(formData.customerInfo.email, personalMessage);
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setIsSending(false);
    }
  };

  const handleSendPaymentLink = async () => {
    if (!contractId) return;
    setIsSendingPayment(true);
    try {
      const res = await fetch(`${API_URL}/api/contract/${contractId}/send-payment-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      if (res.ok) {
        setPaymentSent(true);
        checkStatus();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to send payment link');
      }
    } catch (err) {
      alert('Error sending payment link');
    } finally {
      setIsSendingPayment(false);
    }
  };

  // SENT - Show status dashboard
  if (isSent) {
    const isSigned = contractStatus?.status === 'signed' || contractStatus?.status === 'payment_sent';
    
    return (
      <div className="space-y-6 animate-fade-in">
        {/* Success Banner */}
        <Card className="border-0 shadow-lg bg-gradient-card">
          <CardContent className="pt-6 pb-6">
            <div className="text-center space-y-4">
              <div className="relative mx-auto w-16 h-16">
                <div className="relative w-16 h-16 rounded-full bg-success flex items-center justify-center">
                  <Mail className="w-8 h-8 text-success-foreground" />
                </div>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-1">
                  {language === 'de' ? 'Vertragslink gesendet!' : 'Contract Link Sent!'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {language === 'de' 
                    ? `Eine E-Mail mit dem Unterschriftslink wurde an ${formData.customerInfo.email} gesendet.`
                    : `An email with the signing link was sent to ${formData.customerInfo.email}.`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contract Status Card */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between text-base">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                {language === 'de' ? 'Vertragsstatus' : 'Contract Status'}
              </div>
              <Button variant="ghost" size="sm" onClick={checkStatus}>
                <RefreshCcw className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Status Badge */}
            <div className={`p-4 rounded-lg border ${isSigned ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-center gap-3">
                {isSigned ? (
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                ) : (
                  <Clock className="w-6 h-6 text-amber-600 animate-pulse" />
                )}
                <div>
                  <p className={`font-semibold ${isSigned ? 'text-green-700' : 'text-amber-700'}`}>
                    {isSigned 
                      ? (language === 'de' ? 'Vertrag unterschrieben!' : 'Contract Signed!')
                      : (language === 'de' ? 'Warte auf Unterschrift...' : 'Waiting for signature...')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {isSigned && contractStatus?.signed_at
                      ? `${language === 'de' ? 'Unterschrieben am' : 'Signed on'} ${new Date(contractStatus.signed_at).toLocaleString(locale)}`
                      : (language === 'de' ? 'Der Kunde hat den Link per E-Mail erhalten' : 'The customer received the link by email')}
                  </p>
                </div>
              </div>
            </div>

            {/* Contract Info */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 rounded-lg bg-muted/30 border">
                <p className="text-muted-foreground">{language === 'de' ? 'Kunde' : 'Customer'}</p>
                <p className="font-medium">{formData.customerInfo.name}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30 border">
                <p className="text-muted-foreground">{language === 'de' ? 'Gesamtbetrag' : 'Total'}</p>
                <p className="font-medium text-primary">{formatCurrency(formData.productDetails.totalPrice, locale)}</p>
              </div>
            </div>

            {/* Payment Button - Only show when signed */}
            {isSigned && (
              <div className={`p-4 rounded-lg border ${paymentSent ? 'bg-green-50 border-green-200' : 'bg-primary/5 border-primary/20'}`}>
                {paymentSent ? (
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-semibold text-green-700">
                        {language === 'de' ? 'Zahlungslink gesendet!' : 'Payment Link Sent!'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {language === 'de' 
                          ? 'Der Kunde hat den Zahlungslink per E-Mail erhalten.'
                          : 'The customer received the payment link by email.'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      {language === 'de'
                        ? 'Der Vertrag ist unterschrieben. Möchten Sie dem Kunden den Zahlungslink für die Reservierungsgebühr (1.000 €) senden?'
                        : 'The contract is signed. Would you like to send the customer the payment link for the reservation fee (€1,000)?'}
                    </p>
                    <Button
                      onClick={handleSendPaymentLink}
                      disabled={isSendingPayment}
                      variant="premium"
                      className="w-full"
                    >
                      {isSendingPayment ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {language === 'de' ? 'Wird gesendet...' : 'Sending...'}</>
                      ) : (
                        <><DollarSign className="w-4 h-4 mr-2" /> {language === 'de' ? 'Zahlungslink senden (1.000 €)' : 'Send Payment Link (€1,000)'}</>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // INITIAL - Send form
  return (
    <div className="space-y-6 animate-fade-in">
      <Card className="border-0 shadow-lg bg-gradient-card">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-xl text-foreground">
            <Send className="w-5 h-5 text-primary" />
            {send.title}
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-2">{send.description}</p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Contract Summary */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              {send.contractSummary}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-3 rounded-lg bg-muted/30 border border-border">
                <User className="w-4 h-4 text-primary mb-1" />
                <p className="text-xs text-muted-foreground">{language === 'de' ? 'Kunde' : 'Customer'}</p>
                <p className="text-sm font-medium">{formData.customerInfo.name}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30 border border-border">
                <Package className="w-4 h-4 text-primary mb-1" />
                <p className="text-xs text-muted-foreground">{language === 'de' ? 'Produkt' : 'Product'}</p>
                <p className="text-sm font-medium">{formData.productDetails.quantity}x PowerPack</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30 border border-border">
                <CreditCard className="w-4 h-4 text-primary mb-1" />
                <p className="text-xs text-muted-foreground">{language === 'de' ? 'Gesamtbetrag' : 'Total'}</p>
                <p className="text-sm font-medium text-primary">{formatCurrency(formData.productDetails.totalPrice, locale)}</p>
              </div>
            </div>
          </div>

          {/* Customer Email */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              {send.customerEmail}
            </Label>
            <Input
              type="email"
              value={formData.customerInfo.email}
              readOnly
              className="bg-muted/30"
            />
          </div>

          {/* Personal Message */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              {send.personalMessage}
              <span className="text-xs text-muted-foreground">({language === 'de' ? 'optional' : 'optional'})</span>
            </Label>
            <Textarea
              value={personalMessage}
              onChange={(e) => setPersonalMessage(e.target.value)}
              placeholder={send.personalMessagePlaceholder}
              rows={3}
            />
          </div>

          {/* Info Box */}
          <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
            <p className="text-sm text-amber-800">
              {language === 'de'
                ? 'Der Kunde erhält eine E-Mail mit einem Link zum Vertrag. Dort kann er die Vertragsdetails prüfen, die AGB lesen und den Vertrag digital unterschreiben.'
                : 'The customer will receive an email with a link to the contract. There they can review the details, read the T&Cs, and digitally sign the contract.'}
            </p>
          </div>

          {/* Send Button */}
          <Button 
            onClick={handleSend} 
            disabled={isSending || !formData.customerInfo.email}
            variant="premium"
            size="lg"
            className="w-full"
          >
            {isSending ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {send.sending}</>
            ) : (
              <><Send className="w-5 h-5 mr-2" /> {send.sendButton}</>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default SendToCustomerSection;
